import { useTenant } from "@/lib/tenant-context.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { Select } from "@/components/ui/select.js";
import { Textarea } from "@/components/ui/textarea.js";
import type { IntakeFormProperty } from "@/lib/types.js";

/**
 * Vertical-profile-driven intake form. Renders the basic identity fields
 * (name / phone / email / address) plus every property in
 * `vertical_profile.intake_form_schema.properties` using its declared type.
 *
 * Form state is uncontrolled — the calling component harvests via FormData on
 * submit. Property names are emitted as `vertical:<name>` so the caller can
 * separate vertical fields from identity fields.
 *
 * Backwards-compatibility: if the profile carries `required_customer_fields`
 * (the v0 shape) but no `intake_form_schema`, those fields are rendered as
 * required string inputs — matching the original behaviour.
 */
export function IntakeForm({ initial = {} }: { initial?: Record<string, unknown> }): React.JSX.Element {
  const { profile } = useTenant();

  const verticalFields = (initial.vertical_fields as Record<string, unknown> | undefined) ?? {};
  const schemaProps = profile?.intake_form_schema?.properties ?? {};
  const schemaRequired = new Set(profile?.intake_form_schema?.required ?? []);
  const legacyRequired = profile?.required_customer_fields ?? [];

  // The legacy `required_customer_fields` list is treated as additional required
  // string fields when intake_form_schema is empty; otherwise the schema wins.
  const propertyEntries: Array<[string, IntakeFormProperty, boolean]> =
    Object.keys(schemaProps).length > 0
      ? Object.entries(schemaProps).map(([name, prop]) => [name, prop, schemaRequired.has(name)])
      : legacyRequired.map((name) => [name, { type: "string" }, true] as [string, IntakeFormProperty, boolean]);

  return (
    <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <BasicField label="Name" name="name" required defaultValue={String(initial.name ?? "")} />
      <BasicField label="Phone (E.164)" name="phone_e164" defaultValue={String(initial.phone_e164 ?? "")} />
      <BasicField label="Email" name="email" type="email" defaultValue={String(initial.email ?? "")} />
      <BasicField label="Address" name="address" defaultValue={String(initial.address ?? "")} />
      {propertyEntries.map(([name, property, required]) => (
        <PropertyField
          key={name}
          name={name}
          property={property}
          required={required}
          initialValue={verticalFields[name]}
        />
      ))}
    </fieldset>
  );
}

function PropertyField({
  name,
  property,
  required,
  initialValue
}: {
  name: string;
  property: IntakeFormProperty;
  required: boolean;
  initialValue: unknown;
}): React.JSX.Element {
  const id = `vertical:${name}`;
  const label = property.title ?? prettyLabel(name);
  const description = property.description;

  // Enum → <select>
  if (property.enum && property.enum.length > 0) {
    return (
      <div className="flex flex-col gap-1">
        <Label htmlFor={id}>
          {label}
          {required && " *"}
        </Label>
        <Select
          id={id}
          name={id}
          required={required}
          defaultValue={initialValue !== undefined ? String(initialValue) : ""}
        >
          {!required && <option value="">—</option>}
          {property.enum.map((option) => (
            <option key={String(option)} value={String(option)}>
              {String(option)}
            </option>
          ))}
        </Select>
        {description && <p className="text-[11px] text-muted-foreground">{description}</p>}
      </div>
    );
  }

  // Boolean → checkbox
  if (property.type === "boolean") {
    return (
      <label htmlFor={id} className="flex items-center gap-2 text-sm sm:col-span-2">
        <input
          id={id}
          name={id}
          type="checkbox"
          defaultChecked={initialValue === true || initialValue === "true"}
          className="h-4 w-4 rounded border"
        />
        <span>
          {label}
          {required && " *"}
        </span>
      </label>
    );
  }

  // String with format `date` / `date-time` / etc.
  if (property.type === "string" || property.type === undefined) {
    if (property.format === "date" || property.format === "date-time") {
      return (
        <div className="flex flex-col gap-1">
          <Label htmlFor={id}>
            {label}
            {required && " *"}
          </Label>
          <Input
            id={id}
            name={id}
            type={property.format === "date" ? "date" : "datetime-local"}
            required={required}
            defaultValue={initialValue !== undefined ? String(initialValue) : ""}
          />
          {description && <p className="text-[11px] text-muted-foreground">{description}</p>}
        </div>
      );
    }
    // Long descriptions render as textarea when title hints at "notes" or "history".
    if (/notes|history|allergies|preferences/i.test(label)) {
      return (
        <div className="flex flex-col gap-1 sm:col-span-2">
          <Label htmlFor={id}>
            {label}
            {required && " *"}
          </Label>
          <Textarea
            id={id}
            name={id}
            required={required}
            defaultValue={initialValue !== undefined ? String(initialValue) : ""}
            rows={3}
          />
          {description && <p className="text-[11px] text-muted-foreground">{description}</p>}
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-1">
        <Label htmlFor={id}>
          {label}
          {required && " *"}
        </Label>
        <Input
          id={id}
          name={id}
          type={property.format === "email" ? "email" : property.format === "tel" ? "tel" : property.format === "uri" ? "url" : "text"}
          required={required}
          defaultValue={initialValue !== undefined ? String(initialValue) : ""}
        />
        {description && <p className="text-[11px] text-muted-foreground">{description}</p>}
      </div>
    );
  }

  // Number / integer
  if (property.type === "number" || property.type === "integer") {
    return (
      <div className="flex flex-col gap-1">
        <Label htmlFor={id}>
          {label}
          {required && " *"}
        </Label>
        <Input
          id={id}
          name={id}
          type="number"
          step={property.type === "integer" ? 1 : undefined}
          min={property.minimum}
          max={property.maximum}
          required={required}
          defaultValue={initialValue !== undefined ? String(initialValue) : ""}
        />
        {description && <p className="text-[11px] text-muted-foreground">{description}</p>}
      </div>
    );
  }

  // Fallback: text input.
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>
        {label}
        {required && " *"}
      </Label>
      <Input id={id} name={id} required={required} defaultValue={initialValue !== undefined ? String(initialValue) : ""} />
    </div>
  );
}

function BasicField({
  label,
  name,
  ...rest
}: { label: string; name: string } & React.InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={name}>{label}</Label>
      <Input id={name} name={name} {...rest} />
    </div>
  );
}

function prettyLabel(field: string): string {
  return field
    .split("_")
    .map((word) => word[0]?.toUpperCase() + word.slice(1))
    .join(" ");
}
